import { PublicClientApplication } from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: '7a77813c-c567-4497-a6f1-226fe5e2bb8f',
    authority: 'https://login.microsoftonline.com/29776405-bc2a-48d3-beb8-e99773e38d47',
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const scopes = ['Calendars.Read'];

let initPromise = null;
let msalInstance = null;
// Listeners that get notified after redirect login completes
const loginListeners = [];

function getMsal() {
  if (!initPromise) {
    initPromise = (async () => {
      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      try {
        const resp = await msalInstance.handleRedirectPromise();
        // If we got a response, this is a redirect back from Microsoft login
        if (resp?.account) {
          console.log('MSAL redirect login complete:', resp.account.username);
          loginListeners.forEach(fn => fn(resp.account));
        }
      } catch (e) {
        console.warn('MSAL handleRedirectPromise error (clearing stale state):', e.errorCode);
        Object.keys(sessionStorage).filter(k => k.includes('msal')).forEach(k => sessionStorage.removeItem(k));
        Object.keys(localStorage).filter(k => k.includes('msal') && k.includes('interaction')).forEach(k => localStorage.removeItem(k));
      }
      return msalInstance;
    })();
  }
  return initPromise;
}

// Subscribe to be notified when redirect login completes
export function onMsLoginComplete(fn) {
  loginListeners.push(fn);
}

export async function msGraphLogin() {
  const msal = await getMsal();
  // Use redirect flow — navigates to Microsoft login and back
  await msal.loginRedirect({ scopes });
  // Page will navigate away, so this won't return
}

export async function msGraphLogout() {
  const msal = await getMsal();
  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    // Clear locally and reload — avoids popup issues
    await msal.clearCache();
    window.location.reload();
  }
}

async function getAccessToken() {
  const msal = await getMsal();
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return null;
  try {
    const resp = await msal.acquireTokenSilent({ scopes, account: accounts[0] });
    return resp.accessToken;
  } catch (e) {
    // If silent fails, redirect to get a new token
    console.error('Silent token failed, redirecting:', e.errorCode);
    await msal.acquireTokenRedirect({ scopes, account: accounts[0] });
    return null;
  }
}

export async function fetchCalendarEvents(startDate, endDate) {
  const token = await getAccessToken();
  if (!token) return [];

  const start = new Date(startDate + 'T00:00:00').toISOString();
  const end = new Date(endDate + 'T23:59:59').toISOString();

  const url = `https://graph.microsoft.com/v1.0/me/calendarview`
    + `?startDateTime=${encodeURIComponent(start)}`
    + `&endDateTime=${encodeURIComponent(end)}`
    + `&$select=subject,start,end,isAllDay,location,showAs`
    + `&$orderby=start/dateTime`
    + `&$top=100`;

  try {
    const resp = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!resp.ok) {
      const errorText = await resp.text();
      console.error('Graph API error:', resp.status, errorText);
      throw new Error(`Graph API ${resp.status}`);
    }
    const data = await resp.json();
    console.log('Calendar events fetched:', data.value?.length || 0);
    return (data.value || []).map(evt => ({
      id: evt.id,
      subject: evt.subject,
      startTime: evt.start?.dateTime,
      endTime: evt.end?.dateTime,
      timeZone: evt.start?.timeZone,
      isAllDay: evt.isAllDay,
      location: evt.location?.displayName || '',
      showAs: evt.showAs,
    }));
  } catch (e) {
    console.error('Calendar fetch error:', e);
    return [];
  }
}

export async function checkExistingSession() {
  const msal = await getMsal();
  const accounts = msal.getAllAccounts();
  console.log('MSAL checkExistingSession — accounts:', accounts.length, accounts.map(a => a.username));
  return accounts.length > 0 ? accounts[0] : null;
}
